/**
 * A visit's **When** — the iCalendar model said in plain words
 * (`docs/visits-billing-and-proposal.md` §4, Board 8).
 *
 * Four rows and nothing else: **Starts · Repeats · Ends · Length**. A plumber,
 * a mechanic, a roofer and a porta-pot renter get different *answers* in the
 * same four rows, which is the whole argument against a screen per trade.
 *
 * **Relations are stored; dates are computed** (§2 rule 1). A visit that
 * follows another keeps `(anchor, event, lag, unit)` and never a date of its
 * own; `resolveVisitDates()` is the one function that turns the relations
 * into days, and it is pure so the builder and the server ask it the same
 * question and get the same answer.
 *
 * Semantics, which every function here holds to:
 *
 * - **`done`** is the end of the anchor's **last day on site**
 *   (`anchorStart + anchorLength − 1`); **`starts`** is the anchor's first day.
 * - A follower's first day is `anchorDay + lag` — calendar days, business days
 *   or 7-day weeks. **Lag 0 is the same day**: *After Tear-off is done* starts
 *   the day Tear-off's last day lands, *With Install* the day Install starts.
 *   This is a *target*, not a booking: the schedule books the real day.
 * - **Business days** are Monday to Friday and nothing else — no holidays
 *   (§2 rule 6). Counting from a weekend, the first business day after is the
 *   Monday; lag 0 never shifts.
 * - **Weekdays** apply only to weeks. Empty means the start's own weekday;
 *   several are allowed and each is an occurrence. The first occurrence is the
 *   first listed weekday **on or after** the start.
 * - **Months** keep the start's day of the month, clamped to the month's last
 *   day (the 31st is the 30th or the 28th); **years** keep the month and day
 *   (Feb 29 is Feb 28 in a common year).
 * - **Ends** — `date`: the last occurrence on or before it. `count`: exactly N.
 *   `term_end`: every occurrence **before** the rental's end. `never`:
 *   open-ended, `occurrences` null (∞ wherever it is printed).
 * - **The rental's end** is the first day the units are no longer rented.
 * - **`on_call`** has no date (`state 'open'`): with `never` it is a standing
 *   arrangement, with `count 1` one visit that waits to be called.
 * - **Repeats `dates`**: the list is the schedule and `starts.on` is its
 *   earliest day; Ends and the weekday row do not apply.
 * - **A `term_end` start** (a pick-up) is one visit; Repeats and Ends do not
 *   apply.
 *
 * Dates are plain `YYYY-MM-DD` strings in the tenant's own zone (§2 rule 7):
 * every piece of arithmetic here is on calendar days at midnight UTC and none
 * of it ever sees a time.
 */
import { z } from 'zod';
import { VISIT_ROLES } from './quote-v2.js';
export const VISIT_STARTS_KINDS = ['date', 'after', 'term_end', 'on_call'];
export const VISIT_REPEAT_KINDS = ['none', 'every', 'dates'];
export const VISIT_REPEAT_UNITS = ['day', 'week', 'month', 'year'];
export const VISIT_ENDS_KINDS = ['date', 'count', 'term_end', 'never'];
export const VISIT_LAG_UNITS = ['days', 'business_days', 'weeks'];
export const VISIT_ANCHOR_EVENTS = ['done', 'starts'];
/** A safety stop for a repeat with a far-off end: no visit is ever quoted past this many trips. */
export const MAX_OCCURRENCES = 1000;
// ---------------------------------------------------------------------------
// Calendar arithmetic, on days
// ---------------------------------------------------------------------------
const DAY = 86_400_000;
const toMs = (iso) => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
const fromMs = (ms) => new Date(ms).toISOString().slice(0, 10);
/** `iso` plus `n` calendar days — `calendar.ts`'s own `addDays` does this for the date picker. */
const addDays = (iso, n) => fromMs(toMs(iso) + n * DAY);
/** ISO weekday, 1 = Monday … 7 = Sunday. */
export function isoWeekday(iso) {
    const day = new Date(toMs(iso)).getUTCDay();
    return day === 0 ? 7 : day;
}
/**
 * `n` business days after `iso`: Monday to Friday, no holidays (§2 rule 6).
 * From a Saturday, one business day is the Monday; 0 never shifts.
 */
export function addBusinessDays(iso, n) {
    let at = iso.slice(0, 10);
    let left = n;
    while (left > 0) {
        at = addDays(at, 1);
        if (isoWeekday(at) <= 5)
            left -= 1;
    }
    return at;
}
function daysInMonth(year, month0) {
    return new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
}
/** `iso` plus `months`, keeping the day of the month and clamping it to the month's last day. */
export function addMonthsClamped(iso, months) {
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    const total = (m - 1) + months;
    const year = y + Math.floor(total / 12);
    const month0 = ((total % 12) + 12) % 12;
    const day = Math.min(d, daysInMonth(year, month0));
    return fromMs(Date.UTC(year, month0, day));
}
function lagFrom(base, lag, unit) {
    if (unit === 'business_days')
        return addBusinessDays(base, lag);
    return addDays(base, unit === 'weeks' ? lag * 7 : lag);
}
// ---------------------------------------------------------------------------
// Occurrences of a repeat
// ---------------------------------------------------------------------------
/**
 * The days of an `every` repeat from `start`, until `stop` says so.
 * `stop(day, index)` is asked before a day is kept; true ends the run.
 */
function everyDays(start, repeats, stop) {
    const out = [];
    const every = Math.max(1, Math.round(repeats.every));
    if (repeats.unit === 'week') {
        const days = (repeats.weekdays.length ? [...new Set(repeats.weekdays)] : [isoWeekday(start)])
            .filter((wd) => wd >= 1 && wd <= 7).sort((a, b) => a - b);
        const monday = addDays(start, 1 - isoWeekday(start));
        for (let block = 0; out.length < MAX_OCCURRENCES; block += 1) {
            const weekStart = addDays(monday, block * every * 7);
            for (const wd of days) {
                const day = addDays(weekStart, wd - 1);
                if (day < start)
                    continue;
                if (stop(day, out.length))
                    return out;
                out.push(day);
            }
        }
        return out;
    }
    for (let index = 0; index < MAX_OCCURRENCES; index += 1) {
        const day = repeats.unit === 'day' ? addDays(start, index * every)
            : addMonthsClamped(start, index * every * (repeats.unit === 'year' ? 12 : 1));
        if (stop(day, index))
            return out;
        out.push(day);
    }
    return out;
}
// ---------------------------------------------------------------------------
// resolveVisitDates
// ---------------------------------------------------------------------------
/**
 * Every visit's days, from its relations — the one place a relative visit
 * becomes a date (§4.3). Walks anchors depth-first: a chain that ends at a
 * visit with no day is `floating`; one that comes back to itself is `cycle`.
 * An anchor that is not in the list (it was removed) leaves its follower
 * floating; the server pins such a follower to its last date when the anchor
 * goes (rule 2), so a stored chain never points at nothing.
 */
export function resolveVisitDates(visits, ctx) {
    const byId = new Map(visits.map((visit) => [visit.id, visit]));
    const out = new Map();
    const walking = new Set();
    const resolve = (visit) => {
        const known = out.get(visit.id);
        if (known)
            return known;
        if (walking.has(visit.id))
            return cycle();
        walking.add(visit.id);
        const result = resolveOne(visit);
        walking.delete(visit.id);
        out.set(visit.id, result);
        return result;
    };
    const startOf = (visit) => {
        const { starts } = visit.when;
        if (starts.kind === 'date')
            return { on: starts.on, cycle: false };
        if (starts.kind === 'term_end')
            return { on: ctx.rentalEnd, cycle: false };
        if (starts.kind === 'on_call')
            return { on: null, cycle: false };
        const anchor = byId.get(starts.anchorId);
        if (!anchor)
            return { on: null, cycle: false };
        const resolved = resolve(anchor);
        if (resolved.state === 'cycle')
            return { on: null, cycle: true };
        const actual = ctx.actuals?.get(anchor.id);
        const base = starts.event === 'done'
            ? actual?.doneOn ?? resolved.lastDayOn
            : actual?.startedOn ?? resolved.firstOn;
        return { on: base ? lagFrom(base, starts.lag, starts.unit) : null, cycle: false };
    };
    const resolveOne = (visit) => {
        const { when } = visit;
        const length = Math.max(1, Math.round(when.lengthDays) || 1);
        const start = startOf(visit);
        if (start.cycle)
            return cycle();
        const finish = (firstOn) => firstOn ? addDays(firstOn, length - 1) : null;
        // When called: nothing on the quote decides the day.
        if (when.starts.kind === 'on_call') {
            const occurrences = when.repeats.kind === 'none' ? 1
                : when.ends.kind === 'count' ? Math.max(1, when.ends.count) : null;
            return { state: 'open', firstOn: null, lastDayOn: null, occurrences, dates: [] };
        }
        // A pick-up when the rental ends is one visit, on the rental's end.
        if (when.starts.kind === 'term_end') {
            return start.on
                ? { state: 'dated', firstOn: start.on, lastDayOn: finish(start.on), occurrences: 1, dates: [start.on] }
                : { state: 'floating', firstOn: null, lastDayOn: null, occurrences: 1, dates: [] };
        }
        const { repeats, ends } = when;
        if (repeats.kind === 'dates') {
            const dates = [...new Set(repeats.dates.map((day) => day.slice(0, 10)))].sort();
            return dates.length
                ? { state: 'dated', firstOn: dates[0], lastDayOn: finish(dates[0]), occurrences: dates.length, dates }
                : { state: 'floating', firstOn: null, lastDayOn: null, occurrences: 0, dates: [] };
        }
        if (repeats.kind === 'none') {
            return start.on
                ? { state: 'dated', firstOn: start.on, lastDayOn: finish(start.on), occurrences: 1, dates: [start.on] }
                : { state: 'floating', firstOn: null, lastDayOn: null, occurrences: 1, dates: [] };
        }
        // Every N units.
        if (ends.kind === 'never') {
            const first = start.on ? everyDays(start.on, repeats, (_day, index) => index >= 1)[0] ?? null : null;
            return { state: 'open', firstOn: first, lastDayOn: finish(first), occurrences: null, dates: [] };
        }
        if (!start.on) {
            const occurrences = ends.kind === 'count' ? Math.max(1, ends.count) : null;
            return { state: 'floating', firstOn: null, lastDayOn: null, occurrences, dates: [] };
        }
        let stop;
        if (ends.kind === 'count') {
            const count = Math.max(1, Math.round(ends.count));
            stop = (_day, index) => index >= count;
        }
        else if (ends.kind === 'date') {
            if (!ends.on) {
                const first = everyDays(start.on, repeats, (_day, index) => index >= 1)[0] ?? null;
                return { state: 'floating', firstOn: first, lastDayOn: finish(first), occurrences: null, dates: [] };
            }
            const until = ends.on.slice(0, 10);
            stop = (day) => day > until;
        }
        else {
            if (!ctx.rentalEnd) {
                const first = everyDays(start.on, repeats, (_day, index) => index >= 1)[0] ?? null;
                return { state: 'floating', firstOn: first, lastDayOn: finish(first), occurrences: null, dates: [] };
            }
            const rentalEnd = ctx.rentalEnd.slice(0, 10);
            stop = (day) => day >= rentalEnd;
        }
        const dates = everyDays(start.on, repeats, stop);
        return dates.length
            ? { state: 'dated', firstOn: dates[0], lastDayOn: finish(dates[0]), occurrences: dates.length, dates }
            : { state: 'dated', firstOn: null, lastDayOn: null, occurrences: 0, dates: [] };
    };
    for (const visit of visits)
        resolve(visit);
    return out;
}
function cycle() {
    return { state: 'cycle', firstOn: null, lastDayOn: null, occurrences: null, dates: [] };
}
// ---------------------------------------------------------------------------
// Who follows whom
// ---------------------------------------------------------------------------
/** Every visit that follows `id`, directly or through another — transitive, in walk order. */
export function followersOf(visits, id) {
    const out = [];
    const seen = new Set([id]);
    const queue = [id];
    while (queue.length) {
        const at = queue.shift();
        for (const visit of visits) {
            const { starts } = visit.when;
            if (starts.kind === 'after' && starts.anchorId === at && !seen.has(visit.id)) {
                seen.add(visit.id);
                out.push(visit.id);
                queue.push(visit.id);
            }
        }
    }
    return out;
}
/** Whether `id` following `anchorId` would make a visit follow itself (the Which-visit list leaves these out). */
export function wouldCycle(visits, id, anchorId) {
    return anchorId === id || followersOf(visits, id).includes(anchorId);
}
// ---------------------------------------------------------------------------
// The words — visitWhenPhrase (§4.4)
// ---------------------------------------------------------------------------
const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const monDay = (iso) => new Date(toMs(iso)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const dowMonDay = (iso) => {
    const date = new Date(toMs(iso));
    const dow = date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
    return `${dow} ${monDay(iso)}`;
};
const plural = (n, one, many = `${one}s`) => (n === 1 ? one : many);
const visitsWord = (n) => `${n} ${plural(n, 'visit')}`;
/** `Monday` · `Monday and Wednesday` · `Monday, Wednesday and Friday`. */
function weekdayList(days) {
    const names = [...new Set(days)].sort((a, b) => a - b).map((wd) => WEEKDAY_NAMES[wd - 1]);
    if (names.length <= 1)
        return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`;
}
/** `1 business day` · `30 days` · `2 weeks`. */
function lagWords(lag, unit) {
    const one = unit === 'business_days' ? 'business day' : unit === 'weeks' ? 'week' : 'day';
    return `${lag} ${plural(lag, one)}`;
}
/**
 * `Every week` · `Every 3 weeks` · `Every day` — plus ` on Monday` for weeks
 * whose weekdays were chosen. An empty list means "the start's own weekday",
 * which is a day nobody picked, so it is not said.
 */
function everyWords(repeats) {
    const n = Math.max(1, Math.round(repeats.every));
    let text = n === 1 ? `Every ${repeats.unit}` : `Every ${n} ${repeats.unit}s`;
    if (repeats.unit === 'week' && repeats.weekdays.length)
        text += ` on ${weekdayList(repeats.weekdays)}`;
    return text;
}
/** The popover sub-line's cadence: `Weekly` for every week, `Every 3 weeks` otherwise. */
function everyShort(repeats) {
    const n = Math.max(1, Math.round(repeats.every));
    if (n === 1) {
        return { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' }[repeats.unit];
    }
    return `Every ${n} ${repeats.unit}s`;
}
/** Piece **R** — a relative start that does not repeat: `1 business day after Diagnose is done`. */
export function startsLead(when, names) {
    const { starts } = when;
    if (starts.kind !== 'after')
        return null;
    const anchor = names.get(starts.anchorId) ?? 'another visit';
    if (starts.event === 'done') {
        return starts.lag === 0 ? `After ${anchor} is done`
            : `${lagWords(starts.lag, starts.unit)} after ${anchor} is done`;
    }
    return starts.lag === 0 ? `With ${anchor}` : `${lagWords(starts.lag, starts.unit)} after ${anchor} starts`;
}
/** Piece **R′** — the same, after `from `: `when Core treatment is done` · `the day Install starts`. */
export function startsClause(when, names) {
    const { starts } = when;
    if (starts.kind !== 'after')
        return null;
    const anchor = names.get(starts.anchorId) ?? 'another visit';
    if (starts.event === 'done') {
        return starts.lag === 0 ? `when ${anchor} is done`
            : `${lagWords(starts.lag, starts.unit)} after ${anchor} is done`;
    }
    return starts.lag === 0 ? `the day ${anchor} starts`
        : `${lagWords(starts.lag, starts.unit)} after ${anchor} starts`;
}
/**
 * The one function that writes a schedule (§4.4). The card, the connector
 * chip, the Reads line, the iPad, the phone and the proposal's relative-start
 * sentence all call it, or the two pieces above; a component that spells
 * "after" itself is a defect (do-not-substitute 5).
 */
export function visitWhenPhrase(when, ctx) {
    const { starts, repeats, ends } = when;
    const { resolved } = ctx;
    const length = Math.max(1, Math.round(when.lengthDays) || 1);
    const onSite = length > 1 ? ` · ${length} days on site` : '';
    if (starts.kind === 'on_call') {
        return { card: 'When called', reads: 'When called — scheduled when the customer asks.', short: 'when called' };
    }
    if (starts.kind === 'term_end') {
        const card = `When the rental ends${onSite}`;
        return { card, reads: `${card}.`, short: 'when the rental ends' };
    }
    let card;
    let short;
    if (repeats.kind === 'dates') {
        const dates = resolved.dates;
        card = dates.length > 6
            ? `${visitsWord(dates.length)} · ${monDay(dates[0])} – ${monDay(dates.at(-1))}`
            : `${visitsWord(dates.length)}: ${dates.map(monDay).join(', ')}`;
        short = `${dates.length} ${plural(dates.length, 'date')}`;
    }
    else if (repeats.kind === 'none') {
        if (starts.kind === 'after') {
            card = startsLead(when, ctx.names);
            short = `after ${ctx.names.get(starts.anchorId) ?? 'another visit'}`;
        }
        else {
            card = starts.on ? `On a date · ${dowMonDay(starts.on)}` : 'One-time';
            short = 'One-time';
        }
    }
    else {
        const every = everyWords(repeats);
        const count = resolved.occurrences;
        const endWords = ends.kind === 'term_end' ? 'until the rental ends'
            : ends.kind === 'count' ? visitsWord(Math.max(1, ends.count))
                : ends.kind === 'date' && ends.on ? `until ${monDay(ends.on)}` : null;
        if (starts.kind === 'after') {
            card = [every, `from ${startsClause(when, ctx.names)}`, endWords].filter(Boolean).join(' · ');
        }
        else if (resolved.state === 'dated' && resolved.dates.length) {
            const first = resolved.dates[0];
            const last = resolved.dates.at(-1);
            card = `${every} · ${monDay(first)} – ${monDay(last)} · ${visitsWord(resolved.dates.length)}`;
        }
        else if (ends.kind === 'never') {
            card = resolved.firstOn ? `${every} · from ${monDay(resolved.firstOn)}` : every;
        }
        else if (ends.kind === 'term_end') {
            card = [every, resolved.firstOn ? `from ${monDay(resolved.firstOn)}` : null, 'until the rental ends']
                .filter(Boolean).join(' · ');
        }
        else if (count !== null) {
            card = `${every} · ${visitsWord(count)}`;
        }
        else {
            card = every;
        }
        short = [everyShort(repeats),
            ends.kind === 'term_end' ? 'until the rental ends'
                : count !== null ? visitsWord(count) : null].filter(Boolean).join(' · ');
    }
    card += onSite;
    return { card, reads: `${card}.`, short };
}
/**
 * What the columns nothing has moved off yet must say for a When (§3): the
 * work-item expansion, billing windows and the proposal's projection keep
 * reading them, and for a visit with dates they are correct. Written by
 * `writeVisitWhen` in the same statement as the When, never on read.
 *
 * `qty` for an open-ended visit is whatever it holds today, so the caller
 * passes it.
 */
export function legacyColumnsOf(when, resolved, currentQty) {
    const { starts, repeats } = when;
    const length = Math.max(1, Math.round(when.lengthDays) || 1);
    const trigger = starts.kind === 'term_end' ? 'term_end'
        : starts.kind === 'on_call' ? 'on_call' : 'date';
    const qty = resolved.occurrences === null ? currentQty : Math.max(1, resolved.occurrences);
    if (repeats.kind === 'none' || starts.kind === 'term_end') {
        return {
            schedule_kind: 'one_time',
            starts_on: null,
            ends_on: null,
            // The day of a one-time visit lives in target_on; a computed day is not stored.
            target_on: starts.kind === 'date' ? starts.on : null,
            qty,
            duration_days: length > 1 ? length : null,
            trigger,
        };
    }
    let schedule = 'custom';
    if (repeats.kind === 'every') {
        const n = Math.round(repeats.every);
        const oneWeekday = repeats.weekdays.length <= 1;
        if (repeats.unit === 'week' && n === 1 && oneWeekday)
            schedule = 'weekly';
        else if (repeats.unit === 'week' && n === 2 && oneWeekday)
            schedule = 'biweekly';
        else if (repeats.unit === 'month' && n === 1)
            schedule = 'monthly';
    }
    const dated = resolved.state === 'dated' && resolved.dates.length > 0;
    return {
        schedule_kind: schedule,
        starts_on: dated ? resolved.dates[0] : null,
        ends_on: dated ? resolved.dates.at(-1) : null,
        target_on: null,
        qty,
        duration_days: null,
        trigger,
    };
}
/** Two Whens are the same answer — what "Done with no change writes nothing" compares. */
export function sameWhen(a, b) {
    return JSON.stringify(normaliseWhen(a)) === JSON.stringify(normaliseWhen(b));
}
/** The When with its lists sorted and its days trimmed, so two spellings of one answer compare equal. */
export function normaliseWhen(when) {
    const { starts, repeats, ends } = when;
    return {
        starts: starts.kind === 'date' ? { kind: 'date', on: starts.on ? starts.on.slice(0, 10) : null }
            : starts.kind === 'after' ? {
                kind: 'after', anchorId: starts.anchorId, event: starts.event,
                lag: Math.max(0, Math.round(starts.lag)), unit: starts.unit,
            }
                : { kind: starts.kind },
        repeats: repeats.kind === 'every' ? {
            kind: 'every', every: Math.max(1, Math.round(repeats.every)), unit: repeats.unit,
            weekdays: repeats.unit === 'week' ? [...new Set(repeats.weekdays)].sort((x, y) => x - y) : [],
        }
            : repeats.kind === 'dates'
                ? { kind: 'dates', dates: [...new Set(repeats.dates.map((day) => day.slice(0, 10)))].sort() }
                : { kind: 'none' },
        ends: ends.kind === 'date' ? { kind: 'date', on: ends.on ? ends.on.slice(0, 10) : null }
            : ends.kind === 'count' ? { kind: 'count', count: Math.max(1, Math.round(ends.count)) }
                : { kind: ends.kind },
        lengthDays: Math.max(1, Math.round(when.lengthDays) || 1),
    };
}
const day = (value) => (value ? String(value).slice(0, 10) : null);
/**
 * A visit's When, from its row. The start day lives where the old model kept
 * it — `target_on` for a visit that does not repeat, `starts_on` for one that
 * does — because `writeVisitWhen` writes both from one When (§3) and the
 * proposal still reads `target_on` for "planned for April".
 */
export function whenOfColumns(row) {
    const repeats = row.repeat_kind === 'every'
        ? { kind: 'every', every: row.repeat_every, unit: row.repeat_unit,
            weekdays: (row.repeat_weekdays ?? []).map(Number) }
        : row.repeat_kind === 'dates'
            ? { kind: 'dates', dates: (row.repeat_dates ?? []).map((one) => String(one).slice(0, 10)).sort() }
            : { kind: 'none' };
    let starts;
    if (row.anchor_visit_id && row.anchor_event) {
        starts = { kind: 'after', anchorId: row.anchor_visit_id, event: row.anchor_event,
            lag: row.lag_value, unit: row.lag_unit };
    }
    else if (row.trigger === 'on_call') {
        starts = { kind: 'on_call' };
    }
    else if (row.trigger === 'term_end' && repeats.kind === 'none') {
        starts = { kind: 'term_end' };
    }
    else if (repeats.kind === 'dates') {
        starts = { kind: 'date', on: repeats.dates[0] ?? null };
    }
    else if (repeats.kind === 'none') {
        starts = { kind: 'date', on: day(row.target_on) ?? day(row.starts_on) };
    }
    else {
        starts = { kind: 'date', on: day(row.starts_on) };
    }
    const ends = row.ends_kind === 'count' ? { kind: 'count', count: row.ends_count ?? 1 }
        : row.ends_kind === 'term_end' ? { kind: 'term_end' }
            : row.ends_kind === 'never' ? { kind: 'never' }
                : { kind: 'date', on: day(row.ends_until) };
    return { starts, repeats, ends, lengthDays: row.length_days ?? 1 };
}
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** A When on the wire — `PUT /quotes/:id/visits/:visitId/when`. */
export const visitWhenSchema = z.object({
    starts: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('date'), on: isoDay.nullable() }),
        z.object({
            kind: z.literal('after'), anchorId: z.string().uuid(), event: z.enum(VISIT_ANCHOR_EVENTS),
            lag: z.number().int().min(0).max(3650), unit: z.enum(VISIT_LAG_UNITS),
        }),
        z.object({ kind: z.literal('term_end') }),
        z.object({ kind: z.literal('on_call') }),
    ]),
    repeats: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('none') }),
        z.object({
            kind: z.literal('every'), every: z.number().int().min(1).max(366),
            unit: z.enum(VISIT_REPEAT_UNITS), weekdays: z.array(z.number().int().min(1).max(7)).max(7),
        }),
        z.object({ kind: z.literal('dates'), dates: z.array(isoDay).min(1).max(366) }),
    ]),
    ends: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('date'), on: isoDay.nullable() }),
        z.object({ kind: z.literal('count'), count: z.number().int().min(1).max(MAX_OCCURRENCES) }),
        z.object({ kind: z.literal('term_end') }),
        z.object({ kind: z.literal('never') }),
    ]),
    lengthDays: z.number().int().min(1).max(365),
});
// ---------------------------------------------------------------------------
// Which kinds of visit a company uses (§4.5.1)
// ---------------------------------------------------------------------------
export const BUSINESS_TYPES = ['landscaping', 'repair', 'roofing', 'rentals', 'all', 'custom'];
/**
 * The five starting points, in Board 21's order. A starting point sets the
 * switches and nothing else; any set that matches none of them is `custom`.
 */
export const VISIT_KIND_PRESETS = [
    { key: 'landscaping', kinds: ['work', 'inspect', 'service'] },
    { key: 'repair', kinds: ['work', 'inspect', 'service'] },
    { key: 'roofing', kinds: ['work', 'inspect'] },
    { key: 'rentals', kinds: ['work', 'place', 'service', 'swap', 'pickup'] },
    { key: 'all', kinds: [...VISIT_ROLES] },
];
/** The enabled kinds in the fixed order, `work` always among them. */
export function normaliseVisitKinds(kinds) {
    const on = new Set(kinds);
    on.add('work');
    return VISIT_ROLES.filter((role) => on.has(role));
}
/**
 * What a set of switches is called. Landscaping and Repair are the same three
 * kinds, so the business type a person chose is kept when it still describes
 * the switches, and the first starting point that matches is used otherwise.
 */
export function businessTypeOf(kinds, chosen) {
    const set = normaliseVisitKinds(kinds).join(',');
    const matches = VISIT_KIND_PRESETS.filter((preset) => preset.kinds.join(',') === set);
    if (chosen && matches.some((preset) => preset.key === chosen))
        return chosen;
    return matches[0]?.key ?? 'custom';
}
export const visitKindsSchema = z.object({
    businessType: z.enum(BUSINESS_TYPES).optional(),
    kinds: z.array(z.enum(VISIT_ROLES)).min(1),
});
//# sourceMappingURL=visit-when.js.map